import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

import { ActionWrapper } from './ActionWrapper';

/**
 * MiniTile Component
 * 
 * A compact card component for displaying small content tiles with optional actions.
 * Commonly used for secondary actions or sub-features within larger card layouts.
 * 
 * @example
 * ```tsx
 * <MiniTile 
 *   minHeight={300}
 *   maxWidth={230}
 *   action={<Button variant="outlined">Create</Button>}
 * >
 *   <img src="/image.png" alt="Feature" />
 * </MiniTile>
 * ```
 */
interface MiniTileProps {
  children: React.ReactNode;
  action?: React.ReactNode;
  minHeight?: number | string;
  maxWidth?: number | string;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

export const MiniTile: React.FC<MiniTileProps> = ({
  children,
  action,
  minHeight,
  maxWidth,
  onClick,
  sx,
}) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        display: 'flex',
        minHeight,
        maxWidth,
        width: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s ease-in-out',
        '&:hover': onClick
          ? {
              transform: 'scale(1.02)',
            }
          : {},
        '&:active': onClick
          ? {
              transform: 'scale(0.98)',
            }
          : {},
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        ...sx,
      }}
    >
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'borderColors.primary',
          borderRadius: 3,
          flex: '1',
          backgroundColor: 'background.paper',
          p: 1,
        }}
      >
        {children}
      </Box>

      {action && <ActionWrapper>{action}</ActionWrapper>}
    </Box>
  );
};